import { getHasXfiPods } from './order';
import { flagEnabled } from './featureFlags';

const mergeDevicesAndReturns = (returnDevice, devices) => {
  const device = (
    devices.find(d => d.serialNumber === returnDevice.serialNumber) || { deviceFound: false }
  );
  // if there are no images, then the device is returned
  // and we should show a checkmark
  return Object.assign({ deviceFound: true }, returnDevice, device);
};

export const getHasShipped = status => status !== 'Pending' && status !== 'UPSScanned';
export const getHasShippedLabel = labels => (
  labels.some(({ packageStatus }) => getHasShipped(packageStatus))
);

export const getPackageStatusText = (packageStatus) => {
  /*
  if (packageStatus === 'UPSScanned') {
    return 'was scanned';
  }
  */

  if (packageStatus === 'InTransit') {
    return 'is in transit';
  }

  return 'was received';
};

export const getReturnObject = (returnObj, services) => {
  const mergedDevices = [].concat(returnObj.returnInitiation.devices).map(device => (
    mergeDevicesAndReturns(device, services.devices)
  ));

  const hasReturnedDevice = mergedDevices.some(device => !device.deviceFound);
  const isXfiPods = getHasXfiPods(mergedDevices);

  return {
    ...returnObj,
    returnDataReady: true,
    isXfiPods,
    hasReturnedDevice,
    hasPendingLabels: returnObj.returnInitiation.shippingLabels.every(label => label.packageStatus === 'Pending'),
    returnOrderStatus: (hasReturnedDevice && returnObj.returnOrderStatus === 'In Progress') ? 'Completed' : returnObj.returnOrderStatus,
    returnInitiation: {
      ...returnObj.returnInitiation,
      devices: mergedDevices,
      shippingLabels: returnObj.returnInitiation.shippingLabels.map((label, index) => ({
        ...label,
        hasShipped: getHasShipped(label.packageStatus),
        printLabelButtonText: `Print Shipping Label ${index + 1}`,
        packageStatusText: getPackageStatusText(label.packageStatus),
      })),
    },
  };
};

const getMultipleStatusTextInit = (completed, total, devices) => `Returning ${devices.length} Devices`;

const getMultipleStatusTextDevice = (completed, total, devices) => {
  const returnedDevices = devices.filter(device => !device.deviceFound).length;

  return `Device ${returnedDevices} of ${devices.length}`;
};

const getMultipleStatusTextBox = (completed, total) => {
  if (completed > 1) {
    return `Boxes ${completed} of ${total}`;
  }

  return `Box ${completed} of ${total}`;
};

export const formatDate = (date) => {
  const monthNames = [
    'January', 'February', 'March',
    'April', 'May', 'June', 'July',
    'August', 'September', 'October',
    'November', 'December',
  ];

  const day = date.getDate();
  const monthIndex = date.getMonth();
  const year = date.getFullYear();

  return `${monthNames[monthIndex]} ${day}, ${year}`;
};

export const stepsContent = [
  {
    id: 'Pending',
    title: 'Return Initiated',
    icon: 'check',
    dateCompleteProp: 'labelCreationDate',
    getMultipleStatusText: getMultipleStatusTextInit,
    multipleStatusSuffix: '',
    itemsComplete: 0,
    completedDate: 0,
    showMultipleStatus: true,
  },
  {
    id: 'UPSScanned',
    title: 'Label Created',
    icon: 'box-closed',
    dateCompleteProp: 'upsScannedDate',
    getMultipleStatusText: getMultipleStatusTextBox,
    multipleStatusSuffix: ' Scanned',
    itemsComplete: 0,
    completedDate: 0,
  },
  {
    id: 'InTransit',
    title: 'Package in Transit',
    icon: 'truck',
    dateCompleteProp: 'inTransitDate',
    getMultipleStatusText: getMultipleStatusTextBox,
    multipleStatusSuffix: ' in Transit',
    itemsComplete: 0,
    completedDate: 0,
  },
  {
    id: 'Delivered',
    title: 'Package Received',
    icon: 'box-open',
    dateCompleteProp: 'deliveryDate',
    getMultipleStatusText: getMultipleStatusTextBox,
    multipleStatusSuffix: ' Received',
    itemsComplete: 0,
    completedDate: 0,
  },
  {
    id: 'Complete',
    title: 'Return Complete',
    icon: 'bill',
    getMultipleStatusText: getMultipleStatusTextDevice,
    multipleStatusSuffix: ' Returned',
    itemsComplete: 0,
    completedDate: 0,
  },
];

export const getProgressSteps = (returnData, stepContent) => {
  if (!returnData.returnInitiation) {
    return false;
  }

  const labels = returnData.returnInitiation.shippingLabels || [];
  const progressOrder = stepContent.map(item => item.id);
  const hasMultiple = labels.length > 1;
  const { devices } = returnData.returnInitiation;

  return labels.reduce((result, label) => {
    const activeStep = (
      // if there is a returned device, consider complete
      returnData.hasReturnedDevice
        ? progressOrder.length
        : progressOrder.indexOf(label.packageStatus)
    );

    return result.map((step, index) => {
      const isComplete = index <= activeStep;
      const newStep = Object.assign({}, step, {
        hasMultiple,
      });

      if (isComplete) {
        newStep.isComplete = true;
        newStep.className = 'progress--active';

        if (hasMultiple) {
          newStep.itemsComplete += 1;
        }

        const stepCompleteDate = label[newStep.dateCompleteProp];

        if (stepCompleteDate && stepCompleteDate > newStep.completedDate) {
          newStep.completedDate = stepCompleteDate;
          newStep.completedDateString = formatDate(new Date(stepCompleteDate));
        }
      }

      return newStep;
    });
  }, stepContent).map((step) => {
    // todo iterate once

    const newStep = Object.assign({}, step);

    newStep.multipleStatus = (
      newStep.getMultipleStatusText(newStep.itemsComplete, labels.length, devices)
        + newStep.multipleStatusSuffix
    );

    if (hasMultiple) {
      newStep.showMultipleStatus = step.isComplete;
    }

    return newStep;
  });
};

const override = !!(new URL(window.location).searchParams.get('override'));
export const returnsDisabled = () => !override && flagEnabled('device-returns.returnsDisabled');
